import React, { useState, useEffect, useContext } from "react";
import { 
  AlertTriangle, Filter, CheckCircle, XCircle, RefreshCw, User, 
  Calendar, FileText, Check, X, ShieldAlert 
} from "lucide-react";
import { employeeWorkforceAPI, staffAPI } from "../../api/apiService";
import { NotificationContext } from "../../context/NotificationContext";
import EmptyState from "../../components/EmptyState";

export default function LeaveApprovals() {
  const { addNotification } = useContext(NotificationContext);
  const [leaves, setLeaves] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Filters
  const [selectedStatus, setSelectedStatus] = useState("PENDING");

  const fetchData = async () => {
    setLoading(true);
    try {
      const [leaveRes, staffRes] = await Promise.all([
        employeeWorkforceAPI.getLeaveRequests(),
        staffAPI.getAllStaff()
      ]);
      setLeaves(leaveRes.data);
      setEmployees(staffRes.data);
    } catch (err) {
      console.error(err);
      addNotification("Failed to fetch leave requests", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleApprove = async (id) => {
    if (!window.confirm("Are you sure you want to approve this leave request?")) return;
    setActionLoading(true);
    try {
      await employeeWorkforceAPI.approveLeaveRequest(id);
      addNotification("Leave request approved successfully!", "success");
      // Refresh list
      const res = await employeeWorkforceAPI.getLeaveRequests();
      setLeaves(res.data);
    } catch (err) {
      console.error(err);
      addNotification("Failed to approve leave request", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (id) => {
    const reason = window.prompt("Please enter the reason for rejecting this leave request:", "Rejected by owner");
    if (reason === null) return; // cancelled
    if (!reason.trim()) {
      addNotification("Rejection reason is required.", "warning");
      return;
    }

    setActionLoading(true);
    try {
      await employeeWorkforceAPI.rejectLeaveRequest(id, reason);
      addNotification("Leave request rejected.", "success");
      // Refresh list
      const res = await employeeWorkforceAPI.getLeaveRequests();
      setLeaves(res.data);
    } catch (err) {
      console.error(err);
      addNotification("Failed to reject leave request", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const filteredLeaves = leaves.filter(item => {
    return selectedStatus ? item.status === selectedStatus : true;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900">
        <EmptyState
          preset="leave"
          title="Loading Leave Requests..."
          message="Please wait a moment while we fetch pending approvals."
        />
      </div>
    );
  }

  const statusBadges = {
    "PENDING": "bg-amber-50 text-amber-600 dark:bg-amber-950/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800",
    "APPROVED": "bg-green-50 text-green-600 dark:bg-green-950/30 dark:text-green-400 border border-green-200 dark:border-green-800",
    "REJECTED": "bg-red-50 text-red-600 dark:bg-red-950/30 dark:text-red-400 border border-red-200 dark:border-red-800",
  };

  const leaveTypesMap = {
    "CASUAL": "Casual Leave",
    "SICK": "Sick Leave",
    "EARNED": "Earned Leave",
    "UNPAID": "Unpaid Leave",
    "MATERNITY": "Maternity Leave",
    "PATERNITY": "Paternity Leave",
    "BEREAVEMENT": "Bereavement Leave"
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="bg-white dark:bg-slate-800 p-6 md:p-8 rounded-3xl border border-gray-100 dark:border-slate-700 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-600 rounded-2xl text-white shadow-lg shadow-green-500/20">
              <AlertTriangle className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white">Leave Approval Panel</h1>
              <p className="text-gray-500 dark:text-slate-400 font-medium">Review and process staff time-off request applications</p>
            </div>
          </div>

          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-2xl text-sm font-bold shadow-md shadow-green-500/10 active:scale-95 transition"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh Requests
          </button>
        </div>

        {/* Filter Toolbar */}
        <div className="bg-white dark:bg-slate-800 p-4 rounded-3xl border border-gray-100 dark:border-slate-700 shadow-sm flex flex-wrap gap-3">
          {["PENDING", "APPROVED", "REJECTED", ""].map(status => {
            const isActive = selectedStatus === status;
            return (
              <button
                key={status}
                onClick={() => setSelectedStatus(status)}
                className={`px-5 py-2 rounded-2xl text-xs font-black uppercase tracking-wider transition ${
                  isActive 
                    ? "bg-green-600 text-white shadow-md shadow-green-500/10" 
                    : "bg-gray-50 text-gray-500 hover:bg-gray-100 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800"
                }`}
              >
                {status ? `${status} Requests` : "All Requests"}
              </button>
            );
          })}
        </div>

        {/* Leave Requests Cards List */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredLeaves.length === 0 ? (
            <div className="md:col-span-2 bg-white dark:bg-slate-800 p-12 text-center rounded-3xl border border-gray-100 dark:border-slate-700">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-slate-400 font-black text-lg">No Leave Requests Found</p>
              <p className="text-gray-400 text-sm mt-1">There are no requests matching the selected filter status.</p>
            </div>
          ) : filteredLeaves.map(item => {
            const emp = employees.find(e => String(e.id) === String(item.employee)) || item.employee_details;
            const start = new Date(item.start_date);
            const end = new Date(item.end_date);
            const days = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;

            return (
              <div 
                key={item.id}
                className="bg-white dark:bg-slate-800 p-6 rounded-3xl border border-gray-100 dark:border-slate-700 shadow-sm flex flex-col justify-between hover:shadow-md transition-shadow relative overflow-hidden"
              >
                {/* Status indicator on card edge */}
                <div className={`absolute top-0 left-0 w-2 h-full ${
                  item.status === 'APPROVED' ? 'bg-green-500' : item.status === 'REJECTED' ? 'bg-red-500' : 'bg-amber-500'
                }`} />

                <div className="space-y-4">
                  {/* Card Header */}
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-green-500/10 text-green-600 flex items-center justify-center font-black">
                        {emp?.first_name?.[0] || "E"}{emp?.last_name?.[0] || ""}
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900 dark:text-white">
                          {emp ? `${emp.first_name} ${emp.last_name || ""}` : `Employee ID: ${item.employee}`}
                        </h3>
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                          Salesman ID: {emp?.salesman_id || "N/A"}
                        </p>
                      </div>
                    </div>

                    <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest ${statusBadges[item.status]}`}>
                      {item.status}
                    </span>
                  </div>

                  {/* Dates duration detail */}
                  <div className="bg-gray-50 dark:bg-slate-900/60 p-4 rounded-2xl border border-gray-100 dark:border-slate-800/80 flex justify-between items-center text-sm">
                    <div className="space-y-1">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Requested Duration</p>
                      <p className="font-bold text-gray-800 dark:text-slate-200">
                        {start.toLocaleDateString([], { month: "short", day: "numeric" })} - {end.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="px-3 py-1 bg-green-600 text-white font-black text-xs rounded-lg uppercase tracking-wide">
                        {days} Day{days > 1 ? "s" : ""}
                      </span>
                      <p className="text-[10px] text-gray-400 font-bold mt-1 uppercase tracking-widest">
                        {leaveTypesMap[item.leave_type] || item.leave_type}
                      </p>
                    </div>
                  </div>

                  {/* Reason Text */}
                  <div className="space-y-1">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Reason Statement</p>
                    <p className="text-sm text-gray-600 dark:text-slate-300 font-medium">
                      "{item.reason}"
                    </p>
                  </div>

                  {/* Rejection Note details */}
                  {item.rejection_reason && (
                    <div className="p-3 bg-red-50 dark:bg-red-950/20 text-red-600 dark:text-red-400 rounded-xl text-xs font-semibold border border-red-100 dark:border-red-900/50">
                      Rejection Reason: {item.rejection_reason}
                    </div>
                  )}
                </div>

                {/* Approvals action triggers */}
                {item.status === "PENDING" && (
                  <div className="flex gap-3 mt-6 pt-4 border-t border-gray-100 dark:border-slate-700">
                    <button
                      onClick={() => handleReject(item.id)}
                      disabled={actionLoading}
                      className="flex-1 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl font-bold flex items-center justify-center gap-1.5 transition text-xs border border-red-200 active:scale-95"
                    >
                      <X className="w-4 h-4" />
                      Reject Request
                    </button>
                    <button
                      onClick={() => handleApprove(item.id)}
                      disabled={actionLoading}
                      className="flex-1 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold flex items-center justify-center gap-1.5 transition text-xs shadow-md shadow-green-500/10 active:scale-95"
                    >
                      <Check className="w-4 h-4" />
                      Approve Leave
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

      </div>
    </div>
  );
}
